import React from 'react';
import { Card, CardContent, Chip, Typography, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import { BuildingIDProps } from '../atoms/chargers';
import { powerAtom } from '../atoms/power';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { fpmEndpointLink } from '../../../index';

interface PowerCardProps extends BuildingIDProps {
  /** When provided, show building selector in place of title */
  buildingOptions?: string[];
  buildingOptionsLoading?: boolean;
  onBuildingChange?: (id: string) => void;
}

export const PowerCard: React.FC<PowerCardProps> = ({
  BuildingID,
  buildingOptions,
  buildingOptionsLoading,
  onBuildingChange,
}) => {
  const [powerData] = useAtom(powerAtom);
  const showSelector = buildingOptions && onBuildingChange;
  const shouldRender = !!BuildingID || !!showSelector;

  const buildingKey = BuildingID?.toLowerCase() ?? '';
  const buildingPowerData = buildingKey ? powerData[buildingKey] : undefined;
  const latestPowerData = buildingPowerData && buildingPowerData.length > 0
    ? buildingPowerData[buildingPowerData.length - 1]
    : null;
  const { data: monthPeakData } = useQuery({
    queryKey: ['fpmPowerMonthPeak', BuildingID],
    queryFn: async () => {
      if (!BuildingID) return { monthPeak: 0 };
      const { data } = await axios.get<{ monthPeak?: number }>(`${fpmEndpointLink}power-month-peak`, {
        params: { BuildingID },
      });
      return data;
    },
    enabled: !!BuildingID,
    refetchInterval: 30_000,
  });
  const monthPeak = Number(monthPeakData?.monthPeak ?? 0);
  const monthPeakLabel = Number.isFinite(monthPeak) ? monthPeak.toFixed(4) : '0.0000';
  const sectionTitleSx = {
    fontWeight: 800,
    letterSpacing: '0.06em',
    color: 'text.primary',
    mb: 0.5,
  };
  const chipStyles = {
    power: { backgroundColor: '#DAA520', color: 'common.black' },
    phase: { backgroundColor: 'secondary.main', color: 'secondary.contrastText' },
    energy: { backgroundColor: 'warning.dark', color: 'warning.contrastText' },
    charger: { backgroundColor: 'success.main', color: 'success.contrastText' },
  };

  if (!shouldRender) return null;

  return (
    <Card sx={{ height: '100%', minHeight: 220 }}>
      <CardContent sx={{ p: 3.5, display: 'flex', alignItems: 'center', height: '100%', boxSizing: 'border-box' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {showSelector ? (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="fpm-power-building-label">Building</InputLabel>
                <Select
                  labelId="fpm-power-building-label"
                  value={BuildingID || ''}
                  label="Building"
                  onChange={(e) => onBuildingChange(e.target.value)}
                  displayEmpty
                  renderValue={(v) => v || 'Select a building'}
                >
                  {buildingOptionsLoading ? (
                    <MenuItem value="" disabled>Loading…</MenuItem>
                  ) : !buildingOptions?.length ? (
                    <MenuItem value=""><em>Select a building</em></MenuItem>
                  ) : (
                    buildingOptions.map((id) => (
                      <MenuItem key={id} value={id}>{id}</MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="h6" component="div" sx={{ color: 'text.primary' }}>
                {BuildingID}
              </Typography>
            )}
          </Box>
          {!latestPowerData ? (
            <Typography variant="body2" color="text.secondary">
              No recent power data for {BuildingID || 'selected building'}.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))',
                },
              }}
            >
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Typography variant="overline" sx={sectionTitleSx}>Power</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Grid</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.Grid) || 0).toFixed(4)}`} sx={chipStyles.power} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Load</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.Load) || 0).toFixed(4)}`} sx={chipStyles.power} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Solar</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.Solar) || 0).toFixed(4)}`} sx={chipStyles.power} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Demand</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.Demand) || 0).toFixed(4)}`} sx={chipStyles.power} />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Typography variant="overline" sx={sectionTitleSx}>Phase Power</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">PwrA</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.PwrA) || 0).toFixed(4)}`} sx={chipStyles.phase} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">PwrB</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.PwrB) || 0).toFixed(4)}`} sx={chipStyles.phase} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">PwrC</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.PwrC) || 0).toFixed(4)}`} sx={chipStyles.phase} />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Typography variant="overline" sx={sectionTitleSx}>Energy & Peak</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Import</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.DaykWhImport) || 0).toFixed(4)}`} sx={chipStyles.energy} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Export</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.DaykWhExport) || 0).toFixed(4)}`} sx={chipStyles.energy} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">DayPeak</Typography>
                    <Chip size="small" label={`${(Number(latestPowerData.DayPeak) || 0).toFixed(4)}`} sx={chipStyles.energy} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">MonthPeak</Typography>
                    <Chip size="small" label={monthPeakLabel} sx={chipStyles.energy} />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Typography variant="overline" sx={sectionTitleSx}>Charger Status</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Charging</Typography>
                    <Chip size="small" label={`${Number(latestPowerData.NCharging) || 0}`} sx={chipStyles.charger} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Waiting</Typography>
                    <Chip size="small" label={`${Number(latestPowerData.NWaiting) || 0}`} sx={chipStyles.charger} />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PowerCard;
